import { Options, Splide, SplideSlide } from "@splidejs/react-splide";
import Image from "next/image";
import { getImageUrl } from "@/utils/utils";
import "@splidejs/react-splide/css";
import { useEffect, useRef } from "react";

import styles from "./Carousel.module.css";

export type LandingCarouselProps = {
    slides: {
        src: string;
        alt: string;
    }[];
    hideControls?: boolean;
    hideImages?: boolean;
};

export const LandingCarousel = ({
    slides,
    hideControls,
    hideImages,
}: LandingCarouselProps) => {
    const defaultConfig = {
        hideControls: false,
        hideImages: false,
    };

    const config = {
        ...defaultConfig,
        hideControls: Boolean(hideControls),
        hideImages: Boolean(hideImages),
    };

    const renderSlides = (cover = false) => {
        return slides.map((slide) => (
            <SplideSlide key={slide.src}>
                {cover ? (
                    <div
                        role="img"
                        aria-label={slide.alt}
                        style={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            backgroundImage: `url(${getImageUrl(slide.src)})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    ></div>
                ) : (
                    <Image
                        src={getImageUrl(slide.src)}
                        height={600}
                        width={600}
                        alt={slide.alt}
                    />
                )}
            </SplideSlide>
        ));
    };

    const mainOptions: Options = {
        type: "loop",
        perPage: 1,
        perMove: 1,
        gap: "0",
        pagination: false,
        height: "100%",
        arrows: false,
        role: "group",
    };

    const thumbsOptions: Options = {
        type: "slide",
        rewind: true,
        gap: "1rem",
        pagination: false,
        perPage: 3,
        fixedWidth: 120,
        fixedHeight: 70,
        cover: true,
        focus: "center",
        isNavigation: true,
        arrows: false,
        role: "listbox",
    };

    const mainRef = useRef<Splide>(null);
    const thumbsRef = useRef<Splide>(null);

    const announceSlideChange = (index: number) => {
        // fire global event
        const event = new CustomEvent("slideChange", {
            detail: {
                index,
            },
        });
        window.dispatchEvent(event);
    };

    useEffect(() => {
        // announce the first slide
        // announceSlideChange(0);

        // Define handler function for slide change events
        const handleSlideChangeEvent = (e: Event) => {
            console.log("Slide change event", (e as CustomEvent).detail.index);
            handleSlideChange((e as CustomEvent).detail.index);
        };

        // Add event listener
        window.addEventListener("slideChange", handleSlideChangeEvent);

        // Clean up event listener on unmount
        return () => {
            window.removeEventListener("slideChange", handleSlideChangeEvent);
        };
    }, []);

    const handleSlideChange = (index: number) => {
        if (!config.hideControls) {
            thumbsRef.current?.go(index);
        }
        if (!config.hideImages) {
            mainRef.current?.go(index);
        }
    };

    return (
        <>
            <style>
                {`
                .splide {
                height: 100%;}
                    .splide__track, .splide__track li {
                        height: 100%;
                        width: 100%;
                    }
                    
                    .splide__track--nav>.splide__list>.splide__slide.is-active {
                        border: none;
                    }

                    .splide__slide {
                        border-radius: 10px;
                        border: none;
                    }
            `}
            </style>
            <div
                style={{
                    height: config.hideImages ? undefined : "100%",
                    width: "100%",
                    overflow: "hidden",
                    position: config.hideImages ? "inherit" : "relative",
                    display: config.hideImages ? "inline-block" : "block",
                    minHeight: "110px",
                }}
            >
                {!config.hideImages && (
                    <Splide
                        style={{ height: "100%", width: "100%" }}
                        options={mainOptions}
                        ref={mainRef}
                        onActive={(e) => {
                            announceSlideChange(e.index);
                        }}
                    >
                        {renderSlides(true)}
                    </Splide>
                )}
                {!config.hideControls && (
                    <Splide
                        style={{
                            position: config.hideImages
                                ? undefined
                                : "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 74,
                            padding: "1rem 0",
                        }}
                        onActive={(e) => {
                            announceSlideChange(e.index);
                        }}
                        options={thumbsOptions}
                        ref={thumbsRef}
                    >
                        {renderSlides()}
                    </Splide>
                )}
            </div>
        </>
    );
};
